import {
  type QuizQuestionAnswerEntity,
  type ContentItemEntity,
  QuizQuestionAnswerSingleChoiceSchema,
  QuizQuestionAnswerMultipleChoiceSchema,
  QuizQuestionAnswerMatchingSchema,
  QuizQuestionAnswerSequencingSchema,
  QuizQuestionAnswerTrueFalseSchema,
  type QuizQuestionAnswerSingleChoiceEntity,
  type QuizQuestionAnswerMultipleChoiceEntity,
  type QuizQuestionAnswerTrueFalseEntity,
  type QuizQuestionAnswerMatchingEntity,
  type QuizQuestionAnswerSequencingEntity
} from '@/core/infrastructure/schemas/quiz/QuizQuestionAnswerSchema.ts'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'

export abstract class QuizQuestionAnswer {
  type: LearningQuizQuestionType.Values

  protected constructor(answer: QuizQuestionAnswerEntity) {
    this.type = answer.type
  }

  abstract isValid(): boolean
}

// Classes pour les différents types de réponses
export class QuizQuestionAnswerSingleChoice extends QuizQuestionAnswer {
  label: string
  isCorrect: boolean

  constructor(answer: QuizQuestionAnswerSingleChoiceEntity) {
    super(answer)
    this.label = answer.label
    this.isCorrect = answer.isCorrect
  }

  isValid(): boolean {
    return !!this.label
  }

  public static create(data: unknown): QuizQuestionAnswerSingleChoice {
    return EntityValidator.validateAndCreate(
      QuizQuestionAnswerSingleChoiceSchema,
      data,
      QuizQuestionAnswerSingleChoice,
      'ChoiceAnswer'
    )
  }
}

export class QuizQuestionAnswerMultipleChoice extends QuizQuestionAnswer {
  label: string
  isCorrect: boolean

  constructor(answer: QuizQuestionAnswerMultipleChoiceEntity) {
    super(answer)
    this.label = answer.label
    this.isCorrect = answer.isCorrect
  }

  isValid(): boolean {
    return !!this.label
  }

  public static create(data: unknown): QuizQuestionAnswerMultipleChoice {
    return EntityValidator.validateAndCreate(
      QuizQuestionAnswerMultipleChoiceSchema,
      data,
      QuizQuestionAnswerMultipleChoice,
      'ChoiceAnswer'
    )
  }
}

export class QuizQuestionAnswerTrueFalse extends QuizQuestionAnswer {
  label: string
  isCorrect: boolean

  constructor(answer: QuizQuestionAnswerTrueFalseEntity) {
    super(answer)
    this.label = answer.label
    this.isCorrect = answer.isCorrect
  }

  isValid(): boolean {
    return this.label === 'Vrai' || this.label === 'Faux'
  }

  public static create(data: unknown): QuizQuestionAnswerTrueFalse {
    return EntityValidator.validateAndCreate(
      QuizQuestionAnswerTrueFalseSchema,
      data,
      QuizQuestionAnswerTrueFalse,
      'TrueFalseAnswer'
    )
  }
}

export class QuizQuestionAnswerMatching extends QuizQuestionAnswer {
  left: ContentItemEntity
  right: ContentItemEntity

  constructor(answer: QuizQuestionAnswerMatchingEntity) {
    super(answer)
    this.left = answer.left
    this.right = answer.right
  }

  isValid(): boolean {
    return (
      !!this.left &&
      !!this.left.type &&
      !!this.left.value &&
      !!this.right &&
      !!this.right.type &&
      !!this.right.value
    )
  }

  public static create(data: unknown): QuizQuestionAnswerMatching {
    return EntityValidator.validateAndCreate(
      QuizQuestionAnswerMatchingSchema,
      data,
      QuizQuestionAnswerMatching,
      'MatchingAnswer'
    )
  }
}

export class QuizQuestionAnswerSequencing extends QuizQuestionAnswer {
  data: ContentItemEntity
  order: number

  constructor(answer: QuizQuestionAnswerSequencingEntity) {
    super(answer)
    this.data = answer.data
    this.order = answer.order
  }

  isValid(): boolean {
    return !!this.data && !!this.data.type && !!this.data.value
  }

  public static create(data: unknown): QuizQuestionAnswerSequencing {
    return EntityValidator.validateAndCreate(
      QuizQuestionAnswerSequencingSchema,
      data,
      QuizQuestionAnswerSequencing,
      'SequencingAnswer'
    )
  }
}
